// Runs only while a mobile root is mounted. Do not downgrade secure storage or
// turn a failed read into a new login. Foregrounding replenishes a bounded retry
// budget; backgrounding/unmounting cancels timers and leaves credentials alone.
const RETRY_DELAYS = [1000, 2000, 5000, 10000, 30000];

export function startNativeAuthRecovery({ store, storage, appState, timers = globalThis }) {
  let stopped = false, running = false, timer = null, attempts = 0;
  const active = () => appState.currentState === 'active';
  const needed = () => store.getState()._authHydrationFailed || storage.needsRecovery();
  const cancelTimer = () => {
    if (timer !== null) timers.clearTimeout(timer);
    timer = null;
  };
  const schedule = () => {
    if (!needed()) { cancelTimer(); attempts = 0; return; }
    if (stopped || running || timer !== null || !active() || attempts >= RETRY_DELAYS.length) return;
    timer = timers.setTimeout(recover, RETRY_DELAYS[attempts++]);
  };
  const recover = async () => {
    timer = null;
    if (stopped || running || !active()) return;
    running = true;
    try {
      // Persist the latest in-memory login/rotation (or logout fence) first.
      // A subsequent restore can then only read that latest queued snapshot.
      await storage.retryPending();
      if (!stopped && active() && store.getState()._authHydrationFailed) {
        await store.persist.rehydrate();
      }
    } catch (_) { /* storage remains protected; retry with a bounded delay */ }
    finally {
      running = false;
      if (!needed()) attempts = 0;
      schedule();
    }
  };
  const unsubscribeStore = store.subscribe(schedule);
  const unsubscribeStorage = storage.subscribeRecovery(schedule);
  const subscription = appState.addEventListener('change', state => {
    cancelTimer();
    if (state === 'active') {
      attempts = 0;
      void recover();
    }
  });
  schedule();
  return () => {
    stopped = true;
    cancelTimer();
    unsubscribeStore();
    unsubscribeStorage();
    subscription.remove();
  };
}
